import React, { useState, useMemo } from 'react';
import { AutoComplete } from 'antd';
import type { AutoCompleteProps } from 'antd';

export interface CustomAutoCompleteFieldProps
  extends Omit<AutoCompleteProps<any>, 'options'> {
  options: Array<{ label: string; value: any }>;
  value?: any;
  onChange?: (value: any) => void;
  placeholder?: string;
  allowCustomInput?: boolean;
  maxLength?: number;
  showCount?: boolean;
  style?: React.CSSProperties;
}

const CustomAutoCompleteField: React.FC<CustomAutoCompleteFieldProps> = ({
  options = [],
  value,
  onChange,
  placeholder = 'Search options or type custom...',
  allowCustomInput = true,
  maxLength,
  showCount,
  style,
  ...props
}) => {
  const [inputValue, setInputValue] = useState('');

  // Convert options to AutoComplete format
  const autoCompleteOptions = useMemo(() => {
    return options.map(option => ({
      label: option.label,
      value: option.value,
    }));
  }, [options]);

  // Handle search input
  const handleSearch = (searchValue: string) => {
    setInputValue(searchValue);
  };

  // Handle selection from dropdown
  const handleSelect = (selectedValue: any, option: any) => {
    if (onChange) {
      onChange(selectedValue);
    }
    setInputValue('');
  };

  // Handle custom input (when user types something not in options)
  const handleChange = (inputValue: string) => {
    if (onChange) {
      // If it's a predefined option, use the option value
      const option = options.find(opt => opt.label === inputValue);
      if (option) {
        onChange(option.value);
      } else {
        // If it's custom text, use the input value directly
        onChange(inputValue);
      }
    }
    setInputValue(inputValue);
  };

  // Filter function to show matching options
  const filterOption = (inputValue: string, option: any) => {
    return option?.label?.toLowerCase().includes(inputValue.toLowerCase());
  };

  // Get display value
  const getDisplayValue = () => {
    if (value) {
      // If value is a predefined option value, show the label
      const option = options.find(opt => opt.value === value);
      if (option) {
        return option.label;
      }
      // If it's custom text, show the value directly
      return value;
    }
    return inputValue;
  };

  return (
    <AutoComplete
      value={getDisplayValue()}
      options={autoCompleteOptions}
      onSearch={handleSearch}
      onSelect={handleSelect}
      onChange={handleChange}
      placeholder={placeholder}
      filterOption={filterOption}
      allowClear={true}
      style={{ width: '100%', ...style }}
      maxLength={maxLength}
      notFoundContent={
        allowCustomInput ? (
          <div style={{ padding: '8px', textAlign: 'center' }}>
            <div style={{ color: '#666', marginBottom: '4px' }}>
              No matching options found custom text added
            </div>
          </div>
        ) : (
          'No results found'
        )
      }
      {...props}
    />
  );
};

export { CustomAutoCompleteField };
